// REACT
import { useRef, useState, useEffect, useCallback } from "react";

// MATERIAL UI
import { Box, LinearProgress, List, ListItemButton, Grid } from "@mui/material";

// IMPORTS
import _ from "lodash";

// COMPONENTS
import { FormInput, InputStyleType } from "./FormInput";
import { StyledError } from "../wallets/Modal/Withdraw/Error";

// REDUX
import {
  useGetPlaceDetailMutation,
  useLookUpAddressFromInputQuery,
} from "src/store/features/api/generated";

// TYPES
import { AddressDetails } from "src/store/features/onboarding.slice";

// ICONS
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

interface IAddressData {
  country: string | null;
  city: string | null;
  state: string | null;
  postcode: string | null;
  streetAddress: string | null;
  unit: string | null;
}

interface IAutoAddress {
  onSubmit: (data: IAddressData | null) => void;
  onChange?: (e: any) => void;
  name?: string;
  title?: string;
  placeholder?: string;
  inputSx?: any;
  errorMessage?: string;
}

/**
 *
 * @param {(data: IAddressData | null) => void} onSubmit - The callback function to invoke on submit
 * @param {(e: any) => void} onChange - The optional callback function to invoke on change
 * @param {string} name - The optional name of the input
 * @param {string} title - The optional title of the input
 * @param {string} placeholder - The placeholder of the input
 * @param {Record<string,any>} inputSx - The SX styles of the input
 * @param {string} errorMessage - The optional error message for the input
 * @returns {JSX.Element} - Returns the AutoAddress component
 */
export const AutoAddress = ({
  onSubmit,
  onChange = () => {},
  name,
  placeholder = "Search address...",
  inputSx,
  title,
  errorMessage,
}: IAutoAddress) => {
  const [addressSearch, setAddressSearch] = useState<string>("");
  const [country, setCountry] = useState<string | null>("");
  const [state, setState] = useState<string | null>("");
  const [city, setCity] = useState<string | null>("");
  const [postcode, setPostcode] = useState<string | null>("");
  const [streetAddress, setStreetAddress] = useState<string | null>("");
  const [unit, setUnit] = useState<string | null>("");
  const [hide, setHide] = useState<boolean>(false);

  const { data, isFetching } = useLookUpAddressFromInputQuery(
    { input: addressSearch },
    { skip: !addressSearch },
  );

  const containerRef = useRef<HTMLDivElement | null>(null);

  const [getDetails] = useGetPlaceDetailMutation();

  const debouncedChangeHandler = useCallback(
    _.debounce((input: string) => {
      setAddressSearch(input);
    }, 500),
    [],
  );

  const handleOnChange = (e: any) => {
    onChange && onChange(e);
    setHide(false);
    const inputValue = encodeURIComponent(e.target.value);
    debouncedChangeHandler(inputValue);
  };

  const handleSuggestionClick = async (suggestion: any) => {
    const encodedDescription = encodeURIComponent(suggestion.description);
    setAddressSearch(encodedDescription);
    handlePlaceSelected(suggestion);
    setHide(true);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setHide(true);
    }
  };

  useEffect(() => {
    if (streetAddress === " null") setStreetAddress("");
    const data = { country, city, state, postcode, streetAddress, unit };
    // Check if any of the values are not empty
    const hasValues = Object.values(data).some((value) => !!value);
    onSubmit(hasValues ? data : null);
  }, [country, city, state, postcode, streetAddress, unit]);

  const handlePlaceSelected = (place: any) => {
    // You can further type 'place' based on the Google Places API response.
    const place_id = place.place_id as string;

    getDetails({ getPlaceDetailsDto: { placeid: place_id } })
      .unwrap()
      .then((res: any) => {
        const {
          result: { formatted_address, address_components },
        } = res;

        const getAddressComponent = (type: string, components: any[], abbreviated?: boolean) => {
          const component = components.find((item: any) => item.types.includes(type));
          if (abbreviated) return component ? component.short_name : null;
          return component ? component.long_name : null;
        };

        const details: AddressDetails = {
          fullAddress: formatted_address,
          country: getAddressComponent("country", address_components),
          zipCode: getAddressComponent("postal_code", address_components),
          city: getAddressComponent("locality", address_components),
          concil: getAddressComponent("administrative_area_level_2", address_components),
          street: getAddressComponent("route", address_components),
          streetNumber: getAddressComponent("street_number", address_components),
          unitNumber: getAddressComponent("subpremise", address_components),
          state: getAddressComponent("administrative_area_level_1", address_components, true),
        };

        setCountry(details.country);
        setState(details?.state);
        setCity(details?.city);
        setPostcode(details?.zipCode);
        setStreetAddress(`${details?.streetNumber ?? ""} ${details?.street}`);
        setUnit(details.unitNumber);
      });
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selected = !!data?.predictions?.length;

  return (
    <Grid ref={containerRef}>
      <FormInput
        styleType={errorMessage ? InputStyleType.FORM_ERROR : InputStyleType.FORM}
        title={title}
        placeholder={placeholder}
        onChange={handleOnChange}
        inputSx={inputSx}
        name={name}
        InputAdornment={<ArrowDropDownIcon />}
      />
      {errorMessage && <StyledError>{errorMessage}</StyledError>}
      {isFetching && (
        <Box py={1}>
          <LinearProgress sx={{ height: 3 }} />
        </Box>
      )}
      {!hide && selected && (
        <List dense={true}>
          {data?.predictions.map((suggestion: any) => (
            <ListItemButton
              key={suggestion.place_id}
              onClick={() => handleSuggestionClick(suggestion)}>
              {suggestion.description}
            </ListItemButton>
          ))}
        </List>
      )}
    </Grid>
  );
};
